import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  StatusBar,
  ActivityIndicator,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { homeStrings } from '@/constants/residents';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useResponsive } from '@/hooks/common/useResponsive';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { MarketplaceListing, RecentlyViewedItem } from '@/types/residents/marketplaceListing';
import { ListingCard, MarketplaceMobileLayout, PostItemModal, RecentlyViewedSection, MarketplaceEmptyState, LeaveFeedbackModal } from '@/components/residents/marketplace';
import { 
  calculateGridItemSize, 
  getResponsiveColumns, 
  getResponsiveGridGap,
  getResponsivePadding,
  MARKETPLACE_MAX_CONTENT_WIDTH,
} from '@/utils/marketplace';
import { ResidentMobileHeaderWithMenu } from '@/components/residents/mobile/ResidentMobileHeaderWithMenu';
import { MobileScreenLayout } from '@/components/residents/mobile/MobileScreenLayout';
import { useResidentScreen } from '@/hooks/residents/useResidentScreen';
import { useListingNavigation } from '@/hooks/residents/useListingNavigation';
import { useAppSelector } from '@/store';
import { useGetListingsQuery, useGetRecentlyViewedQuery, useGetPendingFeedbackRequestsQuery, useCompleteFeedbackRequestMutation, FeedbackRequest } from '@/store/api';
import { useToast } from '@/contexts/admin/ToastContext';

type MarketplaceNavigationProp = StackNavigationProp<RootStackParamList, 'CommunityMarketplace'>;

const SupermarketIcon = RESIDENTS_SVG_COMPONENTS.SUPERMARKET;
const UserIcon = RESIDENTS_SVG_COMPONENTS.USER;

export const CommunityMarketplaceScreen: React.FC = () => {
  const navigation = useNavigation<MarketplaceNavigationProp>();
  const { layoutMode } = useResponsive();
  const user = useAppSelector((state) => state.auth.user);
  const propertyId = user?.propertyId;
  const { navigateToListing } = useListingNavigation();
  
  const [isPostItemModalVisible, setIsPostItemModalVisible] = useState(false);
  const [isFeedbackModalVisible, setIsFeedbackModalVisible] = useState(false);
  const [selectedFeedbackRequest, setSelectedFeedbackRequest] = useState<FeedbackRequest | null>(null);
  const { showSuccess, showError } = useToast();

  // Fetch listings from API
  const { 
    data: listings = [], 
    isLoading: isLoadingListings,
    refetch: refetchListings,
  } = useGetListingsQuery({ propertyId });

  // Fetch recently viewed from API
  const { 
    data: recentlyViewedListings = [],
    isLoading: isLoadingRecentlyViewed,
  } = useGetRecentlyViewedQuery();

  // Fetch pending feedback requests
  const { 
    data: pendingFeedbackRequests = [],
  } = useGetPendingFeedbackRequestsQuery();

  const [completeFeedbackRequest, { isLoading: isSubmittingFeedback }] = useCompleteFeedbackRequestMutation();

  // Show feedback modal if there are pending requests (on first load)
  React.useEffect(() => {
    if (pendingFeedbackRequests.length > 0 && !isFeedbackModalVisible && !selectedFeedbackRequest) {
      setSelectedFeedbackRequest(pendingFeedbackRequests[0]);
      setIsFeedbackModalVisible(true);
    }
  }, [pendingFeedbackRequests]);

  // Transform recently viewed listings to expected format
  const recentlyViewed = useMemo<RecentlyViewedItem[]>(() => {
    return recentlyViewedListings.map((listing) => ({
      id: `rv-${listing.id}`,
      image: listing.images?.[0]?.url || '',
      listing,
    }));
  }, [recentlyViewedListings]);

  const isLoading = isLoadingListings || isLoadingRecentlyViewed;
  
  // Responsive layout detection
  const isMobile = layoutMode === 'mobile';
  const isTablet = layoutMode === 'tablet';
  const isDesktop = layoutMode === 'desktop';
  
  // Max content width for tablet/desktop (defined first for itemSize calculation)
  const maxContentWidth = isTablet 
    ? MARKETPLACE_MAX_CONTENT_WIDTH.tablet 
    : isDesktop 
      ? MARKETPLACE_MAX_CONTENT_WIDTH.desktop 
      : undefined;

  // Calculate responsive grid values
  const numColumns = getResponsiveColumns(layoutMode);
  const gridGap = getResponsiveGridGap(layoutMode);
  const contentPadding = getResponsivePadding(layoutMode);
  // For desktop/tablet, pass gridPadding: 0 since ScrollView handles padding and contentWrapper has maxWidth
  const itemSize = calculateGridItemSize({ 
    layoutMode,
    gridPadding: isMobile ? undefined : 0,
    maxContentWidth: isMobile ? undefined : maxContentWidth,
  });

  // Use resident screen hook for navigation handling (for mobile menu)
  const {
    handleNavigate,
    handleLogoPress,
    handleSignOut,
    handleNotificationPress,
  } = useResidentScreen('Marketplace', navigation as any);

  const handlePostItem = () => {
    setIsPostItemModalVisible(true);
  };

  const handleClosePostItemModal = () => {
    setIsPostItemModalVisible(false);
  };

  const handlePostItemSuccess = () => {
    // Refetch listings after successfully posting an item
    refetchListings();
  };

  const handleMyProfile = () => {
    navigation.navigate('MarketplaceProfile');
  };

  const handleListingPress = (listing: MarketplaceListing) => {
    navigateToListing(listing);
  };

  const handleRecentlyViewedPress = (item: RecentlyViewedItem) => {
    navigateToListing(item.listing);
  };

  const handleMenuPress = () => {
    navigation.goBack();
  };

  const handleCloseFeedbackModal = () => {
    setIsFeedbackModalVisible(false);
    setSelectedFeedbackRequest(null);
  };

  const handleSubmitFeedback = async (rating: number, text?: string) => {
    if (!selectedFeedbackRequest) return;
    try {
      await completeFeedbackRequest({
        requestId: selectedFeedbackRequest.id,
        rating,
        text,
      }).unwrap();
      showSuccess('Your feedback has been submitted!');
      handleCloseFeedbackModal();
    } catch (error) {
      showError('Failed to submit feedback. Please try again.');
    }
  };

  // Responsive hero card - more compact on larger screens
  const renderHeroCard = () => (
    <View style={[
      styles.heroCard,
      !isMobile && styles.heroCardLarge,
    ]}>
      <View style={[
        styles.heroIconContainer,
        !isMobile && styles.heroIconContainerLarge,
      ]}>
        <SupermarketIcon 
          width={isMobile ? 48 : 56} 
          height={isMobile ? 48 : 56} 
          color={MARKETPLACE_COLORS.textSecondary} 
        />
      </View>
      <View style={styles.heroContent}>
        <View style={styles.heroTextContainer}>
          <Text style={[
            styles.heroTitle,
            !isMobile && styles.heroTitleLarge,
          ]}>{homeStrings.marketplace.title}</Text>
          <Text style={[
            styles.heroDescription,
            !isMobile && styles.heroDescriptionLarge,
          ]}>{homeStrings.marketplace.description}</Text>
        </View>
        <View style={[
          styles.heroButtons,
          !isMobile && styles.heroButtonsLarge,
        ]}>
          <TouchableOpacity 
            style={[
              styles.primaryButton,
              !isMobile && styles.primaryButtonLarge,
            ]} 
            onPress={handlePostItem}
            activeOpacity={0.8}
          >
            <Text style={[
              styles.primaryButtonText,
              !isMobile && styles.primaryButtonTextLarge,
            ]}>Post an item</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[
              styles.secondaryButton,
              !isMobile && styles.secondaryButtonLarge,
            ]} 
            onPress={handleMyProfile}
            activeOpacity={0.8}
          >
            <UserIcon width={isMobile ? 18 : 20} height={isMobile ? 18 : 20} color={MARKETPLACE_COLORS.accentRed} />
            <Text style={[
              styles.secondaryButtonText,
              !isMobile && styles.secondaryButtonTextLarge,
            ]}>My profile</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  const renderContent = () => (
    <ScrollView 
      style={styles.scrollView} 
      contentContainerStyle={[
        styles.content,
        { padding: contentPadding },
        !isMobile && styles.contentLarge,
      ]}
      showsVerticalScrollIndicator={false}
    >
      {/* Responsive content wrapper for tablet/desktop */}
      <View style={[
        styles.contentWrapper,
        maxContentWidth ? { maxWidth: maxContentWidth, alignSelf: 'center', width: '100%' } : undefined,
      ]}>
        {/* Hero Card */}
        {renderHeroCard()}

        {/* Recently Viewed Section */}
        {recentlyViewed.length > 0 && (
          <RecentlyViewedSection
            items={recentlyViewed}
            onItemPress={handleRecentlyViewedPress}
            layoutMode={layoutMode}
          />
        )}

        {/* Listings Grid */}
        {isLoadingListings ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={MARKETPLACE_COLORS.accentRed} />
          </View>
        ) : listings.length === 0 ? (
          <MarketplaceEmptyState
            title="No listings yet"
            subtitle="Be the first to post an item!"
          />
        ) : (
          <View style={[
            styles.listingsGrid,
            { gap: gridGap },
          ]}>
            {listings.map((listing) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                size={itemSize}
                onPress={() => handleListingPress(listing)}
                showBookmark={false}
              />
            ))}
          </View>
        )}
      </View>
    </ScrollView>
  );

  // Mobile and Tablet layout with ResidentMobileHeaderWithMenu (consistent with other resident screens)
  // Tablet also uses ResidentMobileNavigator, so it should have the hamburger menu
  if (isMobile || isTablet) {
    return (
      <View style={styles.container}>
        <StatusBar barStyle="light-content" />
        
        {/* Header with Navigation Menu */}
        <ResidentMobileHeaderWithMenu
          onLogoPress={handleLogoPress}
          onSignOut={handleSignOut}
          onNavigate={handleNavigate}
          onNotificationPress={handleNotificationPress}
          currentPath="/marketplace"
          currentTitle="Marketplace"
          showNotification={true}
        />

        <MobileScreenLayout
          backgroundColor={MARKETPLACE_COLORS.background}
          contentGap={isTablet ? 32 : 24}
          lightStatusBar={false}
        >
          {/* Responsive content wrapper for tablet */}
          <View style={[
            styles.contentWrapper,
            isTablet && maxContentWidth ? { maxWidth: maxContentWidth, alignSelf: 'center', width: '100%' } : undefined,
          ]}>
            {/* Hero Card */}
            {renderHeroCard()}

            {/* Recently Viewed Section */}
            {recentlyViewed.length > 0 && (
              <RecentlyViewedSection
                items={recentlyViewed}
                onItemPress={handleRecentlyViewedPress}
                layoutMode={layoutMode}
              />
            )}

            {/* Listings Grid */}
            {isLoadingListings ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={MARKETPLACE_COLORS.accentRed} />
              </View>
            ) : listings.length === 0 ? (
              <MarketplaceEmptyState
                title="No listings yet"
                subtitle="Be the first to post an item!"
              />
            ) : (
              <View style={[styles.listingsGrid, { gap: gridGap }]}>
                {listings.map((listing) => (
                  <ListingCard
                    key={listing.id}
                    listing={listing}
                    size={itemSize}
                    onPress={() => handleListingPress(listing)}
                    showBookmark={false}
                  />
                ))}
              </View>
            )}
          </View>
        </MobileScreenLayout>

        <PostItemModal
          visible={isPostItemModalVisible}
          onClose={handleClosePostItemModal}
          onSubmitSuccess={handlePostItemSuccess}
          propertyId={propertyId}
        />
        <LeaveFeedbackModal
          visible={isFeedbackModalVisible}
          feedbackRequest={selectedFeedbackRequest}
          onClose={handleCloseFeedbackModal}
          onSubmit={handleSubmitFeedback}
          isSubmitting={isSubmittingFeedback}
        />
      </View>
    );
  }

  // Desktop layout (no mobile frame, uses DesktopNavigator header)
  return (
    <>
      <View style={[
        styles.container,
        styles.containerLarge,
      ]}>
        {renderContent()}
      </View>
      <PostItemModal
        visible={isPostItemModalVisible}
        onClose={handleClosePostItemModal}
        onSubmitSuccess={handlePostItemSuccess}
        propertyId={propertyId}
      />
      <LeaveFeedbackModal
        visible={isFeedbackModalVisible}
        feedbackRequest={selectedFeedbackRequest}
        onClose={handleCloseFeedbackModal}
        onSubmit={handleSubmitFeedback}
        isSubmitting={isSubmittingFeedback}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
  },
  containerLarge: {
    backgroundColor: MARKETPLACE_COLORS.background,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
    paddingBottom: 40,
    gap: 24,
  },
  contentLarge: {
    gap: 32,
  },
  contentWrapper: {
    gap: 24,
  },
  // Hero Card
  heroCard: {
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderRadius: 16,
    padding: 20,
    flexDirection: 'row',
    gap: 16,
  },
  heroCardLarge: {
    padding: 24,
    gap: 20,
    borderRadius: 20,
  },
  heroIconContainer: {
    width: 48,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  heroIconContainerLarge: {
    width: 56,
    height: 56,
  },
  heroContent: {
    flex: 1,
    gap: 16,
  },
  heroTextContainer: {
    gap: 2,
  },
  heroTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 24,
  },
  heroTitleLarge: {
    fontSize: 18,
    lineHeight: 27,
  },
  heroDescription: {
    fontSize: 14,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 21,
  },
  heroDescriptionLarge: {
    fontSize: 15,
    lineHeight: 22,
  },
  heroButtons: {
    flexDirection: 'row',
    gap: 16,
  },
  heroButtonsLarge: {
    gap: 20,
    flexDirection: 'row',
    maxWidth: 320,
  },
  primaryButton: {
    flex: 1,
    backgroundColor: MARKETPLACE_COLORS.accentRed,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    height: 34,
    justifyContent: 'center',
    alignItems: 'center',
  },
  primaryButtonLarge: {
    height: 40,
    paddingHorizontal: 20,
    borderRadius: 10,
  },
  primaryButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.background,
  },
  primaryButtonTextLarge: {
    fontSize: 14,
  },
  secondaryButton: {
    flex: 1,
    borderWidth: 1,
    borderColor: MARKETPLACE_COLORS.accentRed,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    height: 34,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
  },
  secondaryButtonLarge: {
    height: 40,
    paddingHorizontal: 20,
    borderRadius: 10,
    gap: 8,
  },
  secondaryButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.accentRed,
  },
  secondaryButtonTextLarge: {
    fontSize: 14,
  },
  // Listings Grid
  listingsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignContent: 'flex-start',
    gap: 15,
  },
  // Loading State
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
});
